// packages/web/src/lib/auth/session-manager.ts
// FAANG-GRADE: Enhanced localStorage and cookie operations with automatic refresh and recovery

import { validateToken, decodeTenantId, isLikelyJwt, isStubToken } from './token-manager';
import { ApiClient } from '../api/client';

// FAANG-GRADE: Session state management
interface SessionState {
  token: string | null;
  user: any;
  lastRefresh: number;
  refreshAttempts: number;
}

let sessionCache: SessionState | null = null;
const SESSION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_REFRESH_ATTEMPTS = 3;

// FAANG-GRADE: Enhanced token retrieval with caching and validation
export function getToken(): string | null {
  console.log('[LOGIN_DEBUG][SessionManager] ===== GET TOKEN START =====');
  if (typeof window === 'undefined') {
    console.log('[LOGIN_DEBUG][SessionManager] Server-side, returning null');
    return null;
  }

  try {
    // FAANG-GRADE: Check session cache first
    const now = Date.now();
    if (sessionCache && (now - sessionCache.lastRefresh) < SESSION_CACHE_TTL) {
      console.log('[LOGIN_DEBUG][SessionManager] Using cached session token');
      return sessionCache.token;
    }

    // FAANG-GRADE: Multi-source token retrieval with priority
    let token = null;
    let source = 'none';

    // 1. Primary: localStorage (most reliable)
    const lsToken = localStorage.getItem('token');
    if (lsToken && validateTokenSilently(lsToken)) {
      token = lsToken;
      source = 'localStorage';
    }

    // 2. Secondary: sessionStorage (tab-specific persistence)
    if (!token) {
      const ssToken = sessionStorage.getItem('token');
      if (ssToken && validateTokenSilently(ssToken)) {
        token = ssToken;
        source = 'sessionStorage';
        // Sync to localStorage for consistency
        localStorage.setItem('token', token);
      }
    }

    // 3. Tertiary: Cookies (cross-domain compatibility)
    if (!token) {
      const cookieMatch = document.cookie.match(/(?:^|;\s*)(faang_token|token)=([^;]+)/);
      if (cookieMatch) {
        const cookieToken = decodeURIComponent(cookieMatch[2]);
        if (validateTokenSilently(cookieToken)) {
          token = cookieToken;
          source = 'cookie';
          // Sync to localStorage for consistency
          localStorage.setItem('token', token);
        }
      }
    }

    // FAANG-GRADE: Update session cache
    if (token) {
      sessionCache = {
        token,
        user: getUserFromStorage(),
        lastRefresh: now,
        refreshAttempts: 0
      };
      console.log(`[LOGIN_DEBUG][SessionManager] Token found from ${source}, length: ${token.length}`);
    } else {
      sessionCache = null;
      console.log('[LOGIN_DEBUG][SessionManager] No valid token found');
    }

    console.log('[LOGIN_DEBUG][SessionManager] ===== GET TOKEN END =====');
    return token;
  } catch (error) {
    console.error('[LOGIN_DEBUG][SessionManager] Error retrieving token:', error);
    sessionCache = null;
    return null;
  }
}

// FAANG-GRADE: Silent token validation (no console spam)
function validateTokenSilently(token: string): boolean {
  try {
    const validation = validateToken(token);
    return !validation.isExpired && validation.isValid;
  } catch {
    return false;
  }
}

export function getTenantId(): string {
  if (typeof window === 'undefined') return '';
  try { return localStorage.getItem('tenantId') || ''; } catch { return ''; }
}

export function getSubdomain(): string {
  if (typeof window === 'undefined') return '';
  try {
    const fromStorage = localStorage.getItem('subdomain');
    if (fromStorage) return fromStorage;
    const host = window.location.hostname || '';
    // naive parse: foo.example.com -> foo (avoid localhost / www)
    const parts = host.split('.');
    if (parts.length > 2 && parts[0] !== 'www') return parts[0];

    // Special case: don't treat main production domains as subdomains
    // visionarynow.com, project2.visionarynow.com, etc. should not send subdomain
    if (host.includes('visionarynow.com')) {
      return '';
    }

    return '';
  } catch { return ''; }
}

// FAANG-GRADE: Enhanced token storage with multi-layer persistence
export function setToken(token: string | null) {
  if (typeof window === 'undefined') return;

  try {
    if (token) {
      // Validate token before storing
      const validation = validateToken(token);
      if (validation.isExpired || !validation.isValid) {
        console.warn('[SESSION_MANAGER] Attempted to store invalid/expired token');
        return;
      }

      // Multi-layer storage for redundancy
      localStorage.setItem('token', token);
      sessionStorage.setItem('token', token);

      // Store in httpOnly cookie for server-side access (if available)
      try {
        document.cookie = `faang_token=${encodeURIComponent(token)}; Path=/; Max-Age=${7 * 24 * 60 * 60}; Secure; SameSite=Strict`;
      } catch (cookieError) {
        console.warn('[SESSION_MANAGER] Could not set secure cookie:', cookieError);
      }

      // Extract and store metadata
      const tenantId = decodeTenantId(token);
      if (tenantId) {
        localStorage.setItem('tenantId', tenantId);
        sessionStorage.setItem('tenantId', tenantId);
      }

      // Update session cache
      sessionCache = {
        token,
        user: getUserFromStorage(),
        lastRefresh: Date.now(),
        refreshAttempts: 0
      };

      console.log('[SESSION_MANAGER] Token stored successfully with multi-layer persistence');
    } else {
      // Clear all storage layers
      localStorage.removeItem('token');
      localStorage.removeItem('tenantId');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('tenantId');
      sessionStorage.removeItem('user');

      // Clear cookies
      document.cookie = 'token=; Path=/; Max-Age=0';
      document.cookie = 'faang_token=; Path=/; Max-Age=0';

      // Clear session cache
      sessionCache = null;

      console.log('[SESSION_MANAGER] All authentication data cleared');
    }
  } catch (error) {
    console.error('[SESSION_MANAGER] Error storing token:', error);
  }
}

// FAANG-GRADE: Get user data from storage
function getUserFromStorage(): any {
  try {
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
}

// FAANG-GRADE: Automatic token refresh mechanism
export async function refreshTokenIfNeeded(): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    const token = getToken();
    if (!token) return false;

    const validation = validateToken(token);
    if (!validation.isValid) return false;

    // Refresh if token expires within 5 minutes
    const fiveMinutes = 5 * 60 * 1000;
    const expiresSoon = validation.expiresAt && (validation.expiresAt.getTime() - Date.now()) < fiveMinutes;

    if (validation.isExpired || expiresSoon) {
      console.log('[SESSION_MANAGER] Token needs refresh, attempting automatic refresh');

      if (sessionCache && sessionCache.refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        console.warn('[SESSION_MANAGER] Max refresh attempts reached, forcing logout');
        clearAllAuthData();
        return false;
      }

      // Attempt refresh via API (requires refresh token, not access token)
      try {
        const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
        if (!refreshToken) {
          console.warn('[SESSION_MANAGER] No refreshToken present, skipping refresh attempt');
          return false;
        }

        const data = await ApiClient.post('/auth/refresh', { refreshToken });
        const newAccessToken = data?.tokens?.accessToken || data?.token;
        const newRefreshToken = data?.tokens?.refreshToken;

        if (newAccessToken) {
          setToken(newAccessToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
            sessionStorage.setItem('refreshToken', newRefreshToken);
          }
          console.log('[SESSION_MANAGER] Token refreshed successfully');
          return true;
        }
      } catch (refreshError) {
        console.warn('[SESSION_MANAGER] Token refresh failed:', refreshError);
      }

      // Increment refresh attempts
      if (sessionCache) {
        sessionCache.refreshAttempts++;
      }

      return false;
    }

    return true; // Token is still valid
  } catch (error) {
    console.error('[SESSION_MANAGER] Error during token refresh check:', error);
    return false;
  }
}

// FAANG-GRADE: Session recovery mechanism
export async function attemptSessionRecovery(): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    console.log('[SESSION_MANAGER] Attempting session recovery');

    // Check for recovery tokens in various storage layers
    const recoverySources = [
      () => localStorage.getItem('recovery_token'),
      () => sessionStorage.getItem('recovery_token'),
      () => {
        const match = document.cookie.match(/(?:^|;\s*)recovery_token=([^;]+)/);
        return match ? decodeURIComponent(match[1]) : null;
      }
    ];

    for (const getRecoveryToken of recoverySources) {
      const recoveryToken = getRecoveryToken();
      if (recoveryToken && validateTokenSilently(recoveryToken)) {
        console.log('[SESSION_MANAGER] Found valid recovery token, attempting restoration');

        try {
          // Validate recovery token with server
          const data = await ApiClient.post('/auth/recover-session', {});
          if (data.token) {
            setToken(data.token);
            console.log('[SESSION_MANAGER] Session recovered successfully');
            return true;
          }
        } catch (error) {
          console.warn('[SESSION_MANAGER] Session recovery API call failed:', error);
        }
      }
    }

    console.log('[SESSION_MANAGER] Session recovery failed - no valid recovery tokens found');
    return false;
  } catch (error) {
    console.error('[SESSION_MANAGER] Error during session recovery:', error);
    return false;
  }
}

// FAANG-GRADE: Clear all authentication data
export function clearAllAuthData() {
  if (typeof window === 'undefined') return;

  try {
    // Clear all storage layers
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    localStorage.removeItem('recovery_token');

    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('tenantId');
    sessionStorage.removeItem('recovery_token');

    // Clear cookies
    document.cookie = 'token=; Path=/; Max-Age=0';
    document.cookie = 'faang_token=; Path=/; Max-Age=0';
    document.cookie = 'recovery_token=; Path=/; Max-Age=0';

    // Clear session cache
    sessionCache = null;

    console.log('[SESSION_MANAGER] All authentication data cleared');
  } catch (error) {
    console.error('[SESSION_MANAGER] Error clearing auth data:', error);
  }
}

// FAANG-GRADE: Enhanced token clearing with validation
export function clearExpiredTokens() {
  if (typeof window === 'undefined') return;

  try {
    // Check and clear localStorage
    const lsToken = localStorage.getItem('token');
    if (lsToken) {
      const validation = validateToken(lsToken);
      if (validation.isExpired) {
        console.log('[SESSION_MANAGER] Clearing expired token from localStorage');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tenantId');
      }
    }

    // Check and clear sessionStorage
    const ssToken = sessionStorage.getItem('token');
    if (ssToken) {
      const validation = validateToken(ssToken);
      if (validation.isExpired) {
        console.log('[SESSION_MANAGER] Clearing expired token from sessionStorage');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('tenantId');
      }
    }

    // Check and clear cookies
    const cookieMatch = document.cookie.match(/(?:^|;\s*)(faang_token|token)=([^;]+)/);
    if (cookieMatch) {
      const cookieToken = decodeURIComponent(cookieMatch[2]);
      const validation = validateToken(cookieToken);
      if (validation.isExpired) {
        console.log('[SESSION_MANAGER] Clearing expired token from cookies');
        document.cookie = 'token=; Path=/; Max-Age=0';
        document.cookie = 'faang_token=; Path=/; Max-Age=0';
      }
    }

    // Clear session cache if token is expired
    if (sessionCache?.token) {
      const validation = validateToken(sessionCache.token);
      if (validation.isExpired) {
        sessionCache = null;
      }
    }
  } catch (error) {
    console.error('[SESSION_MANAGER] Error clearing expired tokens:', error);
  }
}

// FAANG-GRADE: Check if user has authentication tokens
export function hasAuthTokens(): boolean {
  if (typeof window === 'undefined') return false;

  // Check session cache first
  if (sessionCache?.token) return true;

  // Check storage layers
  const token = getToken();
  return !!token;
}

// FAANG-GRADE: Initialize session management on page load
export function initializeSessionManagement() {
  if (typeof window === 'undefined') return;

  // Clear expired tokens on initialization
  clearExpiredTokens();

  // Set up periodic token refresh check
  setInterval(async () => {
    await refreshTokenIfNeeded();
  }, 60 * 1000); // Check every minute

  // Listen for storage events (cross-tab synchronization)
  window.addEventListener('storage', (event) => {
    if (event.key === 'token' && !event.newValue) {
      // Token was cleared in another tab
      console.log('[SESSION_MANAGER] Token cleared in another tab, clearing local session');
      clearAllAuthData();
      window.location.reload(); // Force page reload to reset state
    }
  });

  console.log('[SESSION_MANAGER] Session management initialized');
}